import React, { useState, useEffect } from "react";
import { Input, Button, Select, Space, Divider, Typography } from "antd";
import { PlusOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { ImageUploadField } from "./ImageUploadField";

const { TextArea } = Input;
const { Text } = Typography;

const BLOCK_TYPES = [
  { value: "text", label: "Text" },
  { value: "list", label: "Bullet List" },
  { value: "image", label: "Image" },
  { value: "video", label: "Video" }
];

const emptySection = () => ({ title: "", content: [{ type: "text", value: "" }] });
const emptyBlock = (type) => {
  switch (type) {
    case "text":
      return { type: "text", value: "" };
    case "list":
      return { type: "list", items: [""] };
    case "image":
      return { type: "image", url: "", caption: "" };
    case "video":
      return { type: "video", url: "", thumbnailUrl: "" };
    default:
      return { type: "text", value: "" };
  }
};

/**
 * Structured content editor for education articles.
 * Produces JSON: { header, headerSubtext, sections: [{ title, content: [{ type, ... }] }] }
 *
 * Props (compatible with Ant Design Form.Item value/onChange):
 *   value    – current JSON object (or null)
 *   onChange – callback(newValue: object)
 */
const StructuredContentEditor = ({ value, onChange }) => {
  const [data, setData] = useState(value || { header: "", headerSubtext: "", sections: [emptySection()] });

  useEffect(() => {
    if (value && JSON.stringify(value) !== JSON.stringify(data)) {
      setData(value);
    }
  }, [value]);

  const emit = (updated) => {
    setData(updated);
    if (onChange) onChange(updated);
  };

  const updateField = (field, val) => emit({ ...data, [field]: val });

  const updateSection = (idx, field, val) => {
    const sections = [...data.sections];
    sections[idx] = { ...sections[idx], [field]: val };
    emit({ ...data, sections });
  };

  const addSection = () => emit({ ...data, sections: [...data.sections, emptySection()] });
  const removeSection = (idx) => emit({ ...data, sections: data.sections.filter((_, i) => i !== idx) });
  const moveSection = (idx, dir) => {
    const sections = [...data.sections];
    const target = idx + dir;
    if (target < 0 || target >= sections.length) return;
    [sections[idx], sections[target]] = [sections[target], sections[idx]];
    emit({ ...data, sections });
  };

  const updateBlock = (sIdx, bIdx, updates) => {
    const sections = [...data.sections];
    const content = [...sections[sIdx].content];
    content[bIdx] = { ...content[bIdx], ...updates };
    sections[sIdx] = { ...sections[sIdx], content };
    emit({ ...data, sections });
  };

  const addBlock = (sIdx, type) => {
    const sections = [...data.sections];
    sections[sIdx] = { ...sections[sIdx], content: [...sections[sIdx].content, emptyBlock(type)] };
    emit({ ...data, sections });
  };

  const removeBlock = (sIdx, bIdx) => {
    const sections = [...data.sections];
    sections[sIdx] = { ...sections[sIdx], content: sections[sIdx].content.filter((_, i) => i !== bIdx) };
    emit({ ...data, sections });
  };

  // List item helpers
  const updateListItem = (sIdx, bIdx, itemIdx, val) => {
    const sections = [...data.sections];
    const items = [...sections[sIdx].content[bIdx].items];
    items[itemIdx] = val;
    sections[sIdx].content[bIdx] = { ...sections[sIdx].content[bIdx], items };
    emit({ ...data, sections });
  };
  const addListItem = (sIdx, bIdx) => {
    const sections = [...data.sections];
    const items = [...sections[sIdx].content[bIdx].items, ""];
    sections[sIdx].content[bIdx] = { ...sections[sIdx].content[bIdx], items };
    emit({ ...data, sections });
  };
  const removeListItem = (sIdx, bIdx, itemIdx) => {
    const sections = [...data.sections];
    const items = sections[sIdx].content[bIdx].items.filter((_, i) => i !== itemIdx);
    sections[sIdx].content[bIdx] = { ...sections[sIdx].content[bIdx], items };
    emit({ ...data, sections });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div>
        <Text strong style={{ display: "block", marginBottom: 4 }}>
          Article Header
        </Text>
        <Input
          value={data.header || ""}
          onChange={(e) => updateField("header", e.target.value)}
          placeholder="e.g., How Xolair Works"
        />
      </div>
      <div>
        <Text strong style={{ display: "block", marginBottom: 4 }}>
          Header Subtext
        </Text>
        <TextArea
          rows={2}
          value={data.headerSubtext || ""}
          onChange={(e) => updateField("headerSubtext", e.target.value)}
          placeholder="Brief intro paragraph below the header..."
        />
      </div>

      <Divider style={{ margin: "8px 0" }}>Sections</Divider>

      {/* Sections */}
      {data.sections?.map((section, sIdx) => (
        <div key={sIdx} style={{ border: "1px solid #d9d9d9", borderRadius: 8, padding: 12, background: "#fafafa" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <Text strong>Section {sIdx + 1}</Text>
            <Space size={4}>
              <Button
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={sIdx === 0}
                onClick={() => moveSection(sIdx, -1)}
              />
              <Button
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={sIdx === data.sections.length - 1}
                onClick={() => moveSection(sIdx, 1)}
              />
              <Button
                size="small"
                icon={<DeleteOutlined />}
                danger
                disabled={data.sections.length <= 1}
                onClick={() => removeSection(sIdx)}
              />
            </Space>
          </div>

          <Input
            value={section.title || ""}
            onChange={(e) => updateSection(sIdx, "title", e.target.value)}
            placeholder="Section title (e.g., What to Expect)"
            style={{ marginBottom: 8 }}
          />

          {/* Content blocks */}
          {section.content?.map((block, bIdx) => (
            <div
              key={bIdx}
              style={{ marginBottom: 8, padding: 8, background: "#fff", borderRadius: 6, border: "1px solid #f0f0f0" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {BLOCK_TYPES.find((t) => t.value === block.type)?.label || block.type}
                </Text>
                <Button size="small" icon={<DeleteOutlined />} danger onClick={() => removeBlock(sIdx, bIdx)} />
              </div>

              {block.type === "text" && (
                <TextArea
                  rows={3}
                  value={block.value || ""}
                  onChange={(e) => updateBlock(sIdx, bIdx, { value: e.target.value })}
                  placeholder="Paragraph text..."
                />
              )}

              {block.type === "list" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {(block.items || []).map((item, iIdx) => (
                    <div key={iIdx} style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <span style={{ color: "#999", fontSize: 12 }}>•</span>
                      <Input
                        value={item}
                        onChange={(e) => updateListItem(sIdx, bIdx, iIdx, e.target.value)}
                        placeholder="List item..."
                        style={{ flex: 1 }}
                      />
                      <Button
                        size="small"
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => removeListItem(sIdx, bIdx, iIdx)}
                      />
                    </div>
                  ))}
                  <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => addListItem(sIdx, bIdx)}>
                    Add item
                  </Button>
                </div>
              )}

              {block.type === "image" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <ImageUploadField
                    value={block.url || ""}
                    onChange={(url) => updateBlock(sIdx, bIdx, { url })}
                    placeholder="Image URL or upload..."
                  />
                  <Input
                    value={block.caption || ""}
                    onChange={(e) => updateBlock(sIdx, bIdx, { caption: e.target.value })}
                    placeholder="Optional caption"
                  />
                </div>
              )}

              {block.type === "video" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <ImageUploadField
                    value={block.url || ""}
                    onChange={(url) => updateBlock(sIdx, bIdx, { url })}
                    placeholder="Video file URL or upload..."
                    accept="video/mp4,video/webm,video/quicktime"
                  />
                  <ImageUploadField
                    value={block.thumbnailUrl || ""}
                    onChange={(url) => updateBlock(sIdx, bIdx, { thumbnailUrl: url })}
                    placeholder="Video thumbnail image URL or upload..."
                  />
                </div>
              )}
            </div>
          ))}

          {/* Add block */}
          <Select
            placeholder="+ Add content block"
            style={{ width: "100%" }}
            value={null}
            onChange={(type) => addBlock(sIdx, type)}
            options={BLOCK_TYPES}
          />
        </div>
      ))}

      <Button type="dashed" icon={<PlusOutlined />} onClick={addSection} block>
        Add Section
      </Button>
    </div>
  );
};

export { StructuredContentEditor };
